import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import CustomInput from '../../components/TextField/TextFeild';
import CountryCodeDropdown from '../../components/CountryDropDown/CountryDropDown';
import { changeFormStep, updateFormData } from '../../store/slices/registrationSlice';
import StepNavBar from './StepNavBar';

const RegistrationStepsOne = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const initialValues = {
        attributes: {
            stationSchool: '',
            stationWebsite: '',
            stationEmail: '',
            stationPhone: '',
            stationInfoPhone: '',
        },
    };

    const validate = (values) => {
        const errors = { attributes: {} };
        const { stationSchool, stationWebsite, stationEmail, stationPhone, stationInfoPhone } = values.attributes;

        if (!stationSchool) errors.attributes.stationSchool = 'Station name is required';
        if (!stationWebsite) errors.attributes.stationWebsite = 'Website is required';

        if (!stationEmail) {
            errors.attributes.stationEmail = 'Email is required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(stationEmail)) {
            errors.attributes.stationEmail = 'Invalid email address';
        }

        if (!stationPhone) {
            errors.attributes.stationPhone = 'Phone number is required';
        } else if (!/^\d{10,15}$/.test(stationPhone)) {
            errors.attributes.stationPhone = 'Invalid phone number';
        }

        if (!stationInfoPhone) {
            errors.attributes.stationInfoPhone = 'Info phone number is required';
        } else if (!/^\d{10,15}$/.test(stationInfoPhone)) {
            errors.attributes.stationInfoPhone = 'Invalid info phone number';
        }

        return Object.keys(errors.attributes).length ? errors : {};
    };

    const formik = useFormik({
        initialValues,
        validate,
        onSubmit: async (values, { setSubmitting, setErrors }) => {
            try {
                dispatch(updateFormData(values));
                dispatch(changeFormStep({ step: 0, completed: true }));
                navigate('/setup/1');
            } catch (error) {
                setErrors({ _error: error.message });
                setSubmitting(false);
            }
        },
    });

    const handleStationPhoneChange = (phoneNumber) => {
        formik.setFieldValue('attributes.stationPhone', phoneNumber);
    };

    const handleInfoPhoneChange = (phoneNumber) => {
        formik.setFieldValue('attributes.stationInfoPhone', phoneNumber);
    };

    const attributesTouched = formik.touched.attributes || {};
    const attributesErrors = formik.errors.attributes || {};

    return (
        <div className="loginBox">
            <StepNavBar />
            <div className="card w-50">
                <div className="card-body px-0">
                    <div className="cardText">
                        <p className="heading pt-2">Configure your station's identity</p>
                    </div>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="FormDiv pt-4">
                            <div className='row'>
                                <div className="col-md-8">
                                    <div className='py-2'>
                                    <CustomInput
                                        label="Station Name / School Name"
                                        type="text"
                                        id="stationSchool"
                                        name="attributes.stationSchool"
                                        value={formik.values.attributes.stationSchool}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={attributesTouched.stationSchool && attributesErrors.stationSchool}
                                        required
                                        autoComplete="off"
                                    />
                                    </div>
                                    <div className='py-2'>
                                    <CustomInput
                                        label="City & State / Call Letters"
                                        type="text"
                                        id="stationName"
                                        name="attributes.stationName"
                                        value={formik.values.attributes.stationName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={attributesTouched.stationName && attributesErrors.stationName}
                                        required
                                        autoComplete="off"
                                    />
                                    </div>
                                    <div className='py-2'>
                                    <CustomInput
                                        label="Station Description"
                                        type="text"
                                        id="stationDescription"
                                        name="attributes.stationDescription"
                                        value={formik.values.attributes.stationDescription}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={attributesTouched.stationDescription && attributesErrors.stationDescription}
                                        required
                                        autoComplete="off"
                                    />
                                    </div>
                                    <div className='py-2'>
                                    <CustomInput
                                        label="Website"
                                        type="text"
                                        id="stationWebsite"
                                        name="attributes.stationWebsite"
                                        value={formik.values.attributes.stationWebsite}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={attributesTouched.stationWebsite && attributesErrors.stationWebsite}
                                        required
                                        autoComplete="off"
                                    />
                                    </div>
                                    <div className='py-2'>
                                    <CustomInput
                                        label="Station's Email"
                                        type="email"
                                        id="stationEmail"
                                        name="attributes.stationEmail"
                                        value={formik.values.attributes.stationEmail}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={attributesTouched.stationEmail && attributesErrors.stationEmail}
                                        required
                                        autoComplete="off"
                                    />
                                    </div>
                                    <div className="custom-form-group d-flex justify-space-between setup-phone-field">
                                        <CountryCodeDropdown
                                            label="Station's Phone"
                                            phoneNumber={formik.values.attributes.stationPhone}
                                            onPhoneNumberChange={handleStationPhoneChange}
                                        />
                                        <hr
                                            className={attributesTouched.stationPhone && attributesErrors.stationPhone ? 'error-hr-line' : ''}
                                        />
                                        {attributesTouched.stationPhone && attributesErrors.stationPhone && (
                                            <div className="error-text">{attributesErrors.stationPhone}</div>
                                        )}
                                    </div>
                                    <div className="custom-form-group d-flex justify-space-between setup-phone-field">
                                        <CountryCodeDropdown
                                            label="Info Page Phone"
                                            phoneNumber={formik.values.attributes.stationInfoPhone}
                                            onPhoneNumberChange={handleInfoPhoneChange}
                                        />
                                        <hr
                                            className={
                                                attributesTouched.stationInfoPhone && attributesErrors.stationInfoPhone ? 'error-hr-line' : ''
                                            }
                                        />
                                        {attributesTouched.stationInfoPhone && attributesErrors.stationInfoPhone && (
                                            <div className="error-text">{attributesErrors.stationInfoPhone}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-4">

                                </div>
                            </div>
                        </div>
                        <div className="btnDiv mt-5 text-center">
                            <button type="submit" className="default-btn">
                                <span>Join Group</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RegistrationStepsOne;
